import { api } from '@/api'
import type {
  AccessPreviewResponse,
  AssignRoleRequestBody,
  ProductResponse,
  ProductRolesResponse,
  RoleAssignmentResponse,
} from '@/api/access.types'
import type { AccessPreview, CatalogRole, RoleAssignment } from '../types'
import { toAccessPreview, toCatalogRolesForProduct, toRoleAssignment } from '../transform/accessTransformer'

// The IDP assignment-led access model, reached through the BFF proxy. Sibling
// to api/api.ts (the plain user CRUD) — kept separate since this owns a
// different set of admin-API resources (catalog, assignments, access preview).
const PRODUCTS_BASE = '/v1/admin/catalog/products'
const CATALOG_ROLES_BASE = '/v1/admin/catalog/roles'
const ASSIGNMENTS_BASE = '/v1/admin/role-assignments'

// GET /v1/admin/catalog/products → every catalogued product, global across tenants.
export function listProducts(): Promise<string[]> {
  return api.get<ProductResponse[]>(PRODUCTS_BASE)
}

// GET /v1/admin/catalog/roles?product= → the roles assignable within one product
// (global yaml catalog + the caller tenant's custom roles).
export function listCatalogRoles(product: string): Promise<CatalogRole[]> {
  return api
    .get<ProductRolesResponse[]>(CATALOG_ROLES_BASE, { params: { product } })
    .then((response) => toCatalogRolesForProduct(response, product))
}

// GET /v1/admin/role-assignments/{userId} → every (product, roleId) the user
// holds; the Access tab filters to the selected product.
export function listRoleAssignments(userId: string): Promise<RoleAssignment[]> {
  return api
    .get<RoleAssignmentResponse[]>(`${ASSIGNMENTS_BASE}/${userId}`)
    .then((assignments) => assignments.map(toRoleAssignment))
}

// POST /v1/admin/role-assignments/{userId}.
export function assignRole(userId: string, product: string, roleId: string): Promise<void> {
  const body: AssignRoleRequestBody = { product, roleId }
  return api.post(`${ASSIGNMENTS_BASE}/${userId}`, { body }).then(() => undefined)
}

// DELETE /v1/admin/role-assignments/{userId}/{product}/{roleId}.
export function revokeRole(userId: string, product: string, roleId: string): Promise<void> {
  return api.delete(`${ASSIGNMENTS_BASE}/${userId}/${product}/${roleId}`).then(() => undefined)
}

// GET /v1/admin/users/{userId}/access?product= → the target user's full
// resolved entitlement for the product, uncapped by any client's ceiling.
export function getAccessPreview(userId: string, product: string): Promise<AccessPreview> {
  return api
    .get<AccessPreviewResponse>(`/v1/admin/users/${userId}/access`, { params: { product } })
    .then(toAccessPreview)
}
